import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { TopBar } from '@/components/layout/TopBar';
// DTC-L: route the bay-occupancy "now" anchor through the canonical
// clock so the live birdseye placement-decision panel is deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { BAY_TYPE_PICKER_OPTIONS } from '@/utils/bayTypeLabels';
import { supabase } from '@/integrations/supabase/client';
import { floorTicketChangeMatters, realtimeRecoveryAction, rebuildDelayMs, uniqueChannelTopic } from '@/lib/realtime/floorRealtime';
import { LiveUpdatesBanner, nextLiveState, type LiveState } from '@/components/dashboard/LiveUpdatesBanner';
import { useForegroundRecovery } from '@/hooks/useForegroundRecovery';
import { Button } from '@/components/ui/button';
import { Settings, Maximize, Minimize, Move, Maximize2, AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useDate } from '@/contexts/DateContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
import { BirdseyeBayCard } from '@/components/birdseye/BirdseyeBayCard';
import { BirdseyeParkingSpotCard } from '@/components/birdseye/BirdseyeParkingSpotCard';
import { NotYetArrivedPanel } from '@/components/birdseye/NotYetArrivedPanel';
import { LocationIngestConflictsPanel } from '@/components/birdseye/LocationIngestConflictsPanel';
import { BayDetailSheet } from '@/components/birdseye/BayDetailSheet';
import { ParkingSpotDetailSheet } from '@/components/birdseye/ParkingSpotDetailSheet';
import { LiftCapacityWarning } from '@/components/safety/LiftCapacityWarning';
import { LiftVerificationDialog } from '@/components/safety/LiftVerificationDialog';
import { validateLiftSafety } from '@/utils/liftSafetyValidator';
import {
  computeAllowedBaysForTicket,
  isTechBayCompatible,
  formatBayRejectionReason,
} from '@/utils/bayGatingValidator';
import { getBayNowAndNextTickets } from '@/utils/bayScheduleHelpers';
import { useFormFactor } from '@/hooks/use-mobile';
import { usePermissions } from '@/hooks/usePermissions';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { placeByLegacyProjection, placeByPhysicalTruth } from '@/lib/location/model';
import { readShopCurrentLocations } from '@/lib/location/api';
import { buildFloorOccupancy, createLatestReadGate, floorDropRefusal } from '@/lib/location/floorOccupancy';
import { useMapCamera } from '@/hooks/useMapCamera';
import { birdseyeAutoFit } from '@/lib/birdseye/birdseyeFit';
import { CAPACITY_NOT_VERIFIED_TITLE, capacityNotVerifiedDescription, isCapacityNotVerified } from '@/lib/location/liftCapacityNotice';
import { 
  DndContext, 
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';

/**
 * Batch 10: structured PostgREST error logger. Captures the four
 * fields supabase-js returns on the `.error` shape so a 500 in the
 * tickets-load chain pinpoints the offending column / relation /
 * RLS rather than vanishing into a `data || []` fallback.
 *
 *   code     — Postgres SQLSTATE (e.g. '42703' = undefined_column,
 *              '42P01' = undefined_table, 'PGRST301' = ambiguous
 *              FK hint, 'P0001' = raise_exception body,
 *              '57014' = canceling statement due to statement timeout).
 *   message  — short human-readable summary.
 *   details  — full row context / column list / FK path.
 *   hint     — Postgres-suggested remediation (often the most
 *              useful field for triage).
 */
function logSupabaseError(stage: string, err: unknown): void {
  const e = (err ?? {}) as { code?: string; message?: string; details?: string; hint?: string };
  console.error(`[Birdseye] ${stage} load failed`, {
    code: e.code ?? null,
    message: e.message ?? null,
    details: e.details ?? null,
    hint: e.hint ?? null,
  });
}

/**
 * Batch 10b: structured timing logger for the Birdseye ticket query.
 * Records `query` / `shop_id` / `selected_day` / `elapsed_ms` plus
 * `row_count` on success, so the next 57014 (or any regression) has
 * inline before/after evidence in the console. Never logs customer
 * names, vehicle IDs, or any ticket-row payload.
 *
 * Caller passes the awaited PostgREST result; the helper inspects
 * `.error` and `.data`. Returns the result unchanged so it can be
 * spread into the existing data/error destructure.
 */
function logBirdseyeTicketQuery<T>(args: {
  query: string;
  shopId: string;
  selectedDay: string;
  startedAt: number;
  result: { data: T[] | null; error: unknown };
}): { data: T[] | null; error: unknown } {
  const { query, shopId, selectedDay, startedAt, result } = args;
  const elapsedMs = Math.round(performance.now() - startedAt);
  if (result.error) {
    console.error(`[Birdseye] ${query} query failed`, {
      shop_id: shopId,
      selected_day: selectedDay,
      elapsed_ms: elapsedMs,
      error: result.error,
    });
  } else {
    // INFO-level for the happy path so the timing trail is visible
    // when DevTools is set to default verbosity. Drop to console.debug
    // if this becomes too chatty in long-running sessions.
    console.info(`[Birdseye] ${query} query ok`, {
      shop_id: shopId,
      selected_day: selectedDay,
      elapsed_ms: elapsedMs,
      row_count: result.data?.length ?? 0,
    });
  }
  return result;
}

const Birdseye = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeShop } = useAuth();
  // DCC-A3: widened useDate() destructure to include canonical
  // `selectedDayLabel` (shop-tz YYYY-MM-DD string SoT). Pre-DCC-A3
  // this file destructured only `selectedDate` (Date) and derived
  // the DB-binding string at L170 via `format(selectedDate, 'yyyy-
  // MM-dd')` — equivalent in same-TZ deployments, but doctrinally
  // indirect. The DateContext doc-block explicitly states "Do NOT
  // use this for database queries" for the Date object. Display-only
  // formatters (e.g. L1024 `format(selectedDate, 'EEEE, MMMM d,
  // yyyy')`) remain unchanged — DateContext permits Date for UI.
  const { selectedDate, selectedDayLabel } = useDate();
  const { shopTimezone } = useShopTimezone();
  const { toast } = useToast();
  const formFactor = useFormFactor();
  const isTouchDevice = formFactor !== 'desktop';
  const { canAssignTickets, canEditShopFloor } = usePermissions();
  // CORE-AU: a move refused because the vehicle's weight is not verified for the lift.
  const [liftVerify, setLiftVerify] = useState<{ ticketId: string; bayId: string; bayName?: string } | null>(null);
  const [bays, setBays] = useState<any[]>([]);
  const [parkingSpots, setParkingSpots] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  // Batch 10: surface PostgREST 500s instead of silently falling
  // through to an empty page. Banner clears the moment the next
  // loadTickets succeeds.
  const [ticketLoadError, setTicketLoadError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  // Batch 10b: ref-mirror of the parking-spots array so the
  // ticket-enrichment lookup inside loadTickets can read the latest
  // list without listing `parkingSpots` in the useCallback dep set.
  // Pre-bump, `parkingSpots` was a callback dep → every successful
  // `loadParkingSpots()` rebuilt `loadTickets` identity → the
  // realtime channel useEffect (deps include `loadTickets`)
  // torn-down + re-subscribed on every parking refresh → CLOSED log
  // spam + a fresh `loadTickets()` immediately fires inside the new
  // effect. The ref decouples that read from the callback identity.
  const parkingSpotsRef = useRef<any[]>([]);
  useEffect(() => {
    parkingSpotsRef.current = parkingSpots;
  }, [parkingSpots]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedBayId, setSelectedBayId] = useState<string | null>(null);
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  
  
  // Lift safety warning state
  const [liftWarningData, setLiftWarningData] = useState<{
    gvwr: number;
    capacity: number;
    compatibleLifts: Array<{ bayId: string; bayName: string; liftCapacityLbs: number; bayType?: string }>;
    ticketId: string;
  } | null>(null);

  // Move ticket mode
  const moveTicketId = searchParams.get('moveTicketId');
  const ticketToMove = moveTicketId ? tickets.find(t => t.id === moveTicketId) : null;
  // #15: the ONE occupancy answer — cards, detail sheets and drops all read it.
  const occupancy = useMemo(() => buildFloorOccupancy(tickets), [tickets]);
  
  // dnd-kit state
  const [isDraggingTicket, setIsDraggingTicket] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [isMovingBay, setIsMovingBay] = useState(false);
  
  // dnd-kit sensors with activation constraints for mobile
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 }
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { distance: 10 }
  });
  const sensors = useSensors(pointerSensor, touchSensor);
  
  // Pan / zoom camera (item #14: shared with the Guided Start bay picker).
  // Wheel, drag, one-finger pan and pinch are paused while dnd-kit owns the
  // pointer (a ticket drag).
  const mapItems = useMemo(() => [
    ...bays.map(b => ({ x: b.position_x, y: b.position_y, w: b.width, h: b.height })),
    ...parkingSpots.map(s => ({ x: s.position_x, y: s.position_y, w: s.width, h: s.height })),
  ], [bays, parkingSpots]);
  const camera = useMapCamera({ items: mapItems, panBlocked: isDraggingTicket, attachKey: isLoading });
  const { containerRef, zoom, offset, bounds, isDragging, zoomRef, fitToScreen } = camera;
  const hasFittedRef = useRef(false);
  // OCT28 RECOVERY #4: the automatic fit frames the bays when the whole lot would draw them too small (phones,
  // tablets, landscape) — src/lib/birdseye/birdseyeFit.ts. "Fit to Screen" still shows everything.
  const bayRects = useMemo(() => bays.map(b => ({ x: b.position_x, y: b.position_y, w: b.width, h: b.height })), [bays]);
  const autoFit = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    fitToScreen(birdseyeAutoFit(el.clientWidth, el.clientHeight, mapItems, bayRects));
  }, [containerRef, fitToScreen, mapItems, bayRects]);


  // #15: re-reads overlap (realtime bursts, the sender's own reload, a
  // foreground return). Latest read wins, so a slow older read can never land
  // last and move a car backward.
  const baysGate = useRef(createLatestReadGate()).current;
  const spotsGate = useRef(createLatestReadGate()).current;
  const ticketsGate = useRef(createLatestReadGate()).current;

  const loadBays = useCallback(async () => {
    if (!activeShop) return;
    const token = baysGate.begin();
    const { data } = await supabase.from('shop_bays').select('*').eq('shop_id', activeShop.id).eq('is_active', true).neq('bay_category', 'parking');
    if (data && baysGate.commit(token)) setBays(data);
  }, [activeShop?.id]);

  const loadParkingSpots = useCallback(async () => {
    if (!activeShop) return;
    const token = spotsGate.begin();

    const [{ data: spotsData }, { data: legacyParkingBays }] = await Promise.all([
      supabase
        .from('parking_lot_spots')
        .select('*')
        .eq('shop_id', activeShop.id)
        .eq('is_active', true),
      supabase
        .from('shop_bays')
        .select('id, name, position_x, position_y, width, height')
        .eq('shop_id', activeShop.id)
        .eq('is_active', true)
        .eq('bay_category', 'parking'),
    ]);

    if (!spotsData) return;

    const legacyById = new Map((legacyParkingBays || []).map((bay: any) => [bay.id, bay]));
    const legacyByLabel = new Map((legacyParkingBays || []).map((bay: any) => [bay.name, bay]));

    const normalizedSpots = spotsData.map((spot: any) => {
      const legacyMatch = legacyById.get(spot.id) || legacyByLabel.get(spot.label);
      const hasRealCoordinates =
        typeof spot.position_x === 'number' &&
        typeof spot.position_y === 'number' &&
        !(spot.position_x === 0 && spot.position_y === 0 && legacyMatch);

      if (!legacyMatch || hasRealCoordinates) return spot;

      return {
        ...spot,
        position_x: legacyMatch.position_x,
        position_y: legacyMatch.position_y,
        width: spot.width ?? legacyMatch.width,
        height: spot.height ?? legacyMatch.height,
      };
    });

    if (!spotsGate.commit(token)) return;
    parkingSpotsRef.current = normalizedSpots;
    setParkingSpots(normalizedSpots);
  }, [activeShop?.id]);

  const loadTickets = useCallback(async () => {
    if (!activeShop) return;
    // DCC-A3: route DB-binding date string through canonical SoT
    // (`selectedDayLabel`) instead of `format(selectedDate, 'yyyy-MM-
    // dd')`. Behavior unchanged when browser-tz === shop-tz; cross-TZ
    // deployments now read shop-tz-canonical date for the .gte/.lte
    // filters at L207-208 below.
    const selectedDateStr = selectedDayLabel;
    // Batch 10b: explicit column allowlist for `tickets.*`. Pre-bump
    // PostgREST returned every column (~50+) for every row of both
    // queries, multiplied by the heavy embed and the duplicate-row
    // fetch (a parked ticket scheduled today appeared in both result
    // sets). The 22 columns below are the exact set the Birdseye
    // renderer reads — verified by a fresh audit of the transform
    // (L297-313), BirdseyeBayCard.tsx, BirdseyeParkingSpot.tsx, the
    // timer/tooltip overlay, the move-mode banner, and the
    // vehicle-present helper. Any non-Birdseye consumer of `tickets`
    // continues to use its own SELECT clause; this allowlist is local
    // to the Birdseye loadTickets() path.
    const ticketColumns = [
      'id',
      'shop_id',
      'scheduled_date',
      'status',
      'assigned_tech_id',
      'scheduled_tech_id',
      'bay_id',
      'parking_spot_id',
      'parking_lot',
      'predicted_minutes',
      'pause_reason',
      'customer_name',
      'start_time',
      'actual_start_time',
      'actually_started',
      'timer_status',
      'opti_time',
      'opti_finish',
      'run_long_minutes',
      'deleted_at',
      'picked_up_at',
      'vehicle_id',
      // Stage B/F: which ticket holds this car's physical truth. ALIASED on
      // purpose: under their own names these would switch on the bay-card
      // rollover exclusion (bayScheduleHelpers) and hide a running
      // continuation that is really in the bay; placement below already
      // draws every dependent where its car is.
      'location_parent_id:parent_ticket_id',
      'location_rollover_id:rollover_of_ticket_id',
    ].join(',');
    const ticketSelect = `
        ${ticketColumns},
        vehicle:vehicles(id, year, make, model, engine_size),
        ticket_jobs(
          minutes_override,
          notes,
          teardown_enabled,
          teardown_complete,
          parts_eta,
          allowed_bay_types_snapshot,
          excluded_bay_types_snapshot,
          required_capabilities_snapshot,
          job:jobs(id, name, standard_minutes)
        ),
        technician:technicians!tickets_assigned_tech_id_fkey(id, name, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, shift_end),
        scheduled_technician:technicians!tickets_scheduled_tech_id_fkey(id, name),
        bay:shop_bays!bay_id(id, name)
      `;

    // Batch 10b: unify the pre-bump `todays_tickets` + `parked_tickets`
    // pair into a single SELECT with a top-level OR over the two
    // predicate sets. Server pays the join + serialize work ONCE per
    // matching row instead of twice for any ticket that was scheduled
    // today AND parked. PostgREST `.or()` accepts nested `and(...)`
    // expressions (gold reference: `src/utils/eodChecker.ts:207`).
    //
    // Branch A — "today's tickets":
    //   status IN ('Active','Paused','Completed')
    //   AND scheduled_date BETWEEN today AND today
    //
    // Branch B — "parked tickets":
    //   parking_spot_id IS NOT NULL
    //   AND deleted_at IS NULL
    //   AND picked_up_at IS NULL
    //   AND status NOT IN ('Cancelled','Deleted')
    //
    // The outer `eq('shop_id', X)` applies to BOTH branches (PostgREST
    // ANDs each `.eq()` to whatever the `.or()` produces). Status
    // values use bare tokens — PostgREST treats them as text without
    // requiring quoting since the enum values contain no reserved
    // characters; this matches the syntax of the pre-bump `.in()` form
    // already used at the same column.
    //
    // CST-A1 cleanup preserved: the dead `scheduledTicketsRes` query
    // that filtered by status IN ('Scheduled','Pending') is gone.
    // R-LOC-5 invariant preserved: parked branch still excludes
    // Cancelled/Deleted defensively.
    // Stage B/F: where each car IS comes from current physical truth, read
    // alongside the tickets (one narrow indexed read, not the event log).
    const token = ticketsGate.begin();
    const physicalTruth = readShopCurrentLocations(activeShop.id).then(
      (m) => m,
      (e: unknown) => {
        console.error('[Birdseye] current vehicle locations unavailable', e);
        return null;
      },
    );
    const startedAt = performance.now();
    const unifiedRes = logBirdseyeTicketQuery({
      query: 'birdseye_tickets_unified',
      shopId: activeShop.id,
      selectedDay: selectedDateStr,
      startedAt,
      result: await supabase
        .from('tickets')
        .select(ticketSelect)
        .eq('shop_id', activeShop.id)
        .or(
          [
            `and(status.in.(Active,Paused,Completed),scheduled_date.gte.${selectedDateStr},scheduled_date.lte.${selectedDateStr})`,
            'and(parking_spot_id.not.is.null,deleted_at.is.null,picked_up_at.is.null,status.not.in.(Cancelled,Deleted))',
          ].join(','),
        ),
    });

    if (unifiedRes.error) {
      logSupabaseError('birdseye_tickets_unified', unifiedRes.error);
      setTicketLoadError((unifiedRes.error as { message?: string } | null)?.message ?? 'Unknown PostgREST error');
      // #15: a failed re-read is not an empty floor. Keep the last truth on
      // screen (behind the banner) rather than draw every spot as free.
      return;
    }

    const unifiedRows = (unifiedRes.data || []) as any[];
    // Server already deduplicates within a single result set; the
    // pre-bump client-side `ticketMap` is no longer necessary, but
    // we keep an id-keyed pass to defend against any future PostgREST
    // quirk that returns the same row twice across the OR branches.
    const ticketMap = new Map<string, any>();
    // Stage B/F: every ticket is PLACED by current physical truth before
    // anything draws it. A planned bay (booking, specialty task, rollover
    // continuation) never puts a car in a bay; no truth = drawn nowhere.
    const currentByTicket = await physicalTruth;
    // #15: a car physically in a bay or spot is drawn even when its ticket is
    // outside the day's set (another day's ticket pulled in early). Only the
    // holders no loaded ticket already draws are fetched — so a car is never
    // drawn twice — and only when there are any.
    if (currentByTicket) {
      const drawn = new Set<string>();
      for (const t of unifiedRows) {
        drawn.add(t.id);
        drawn.add(t.location_parent_id ?? t.location_rollover_id ?? t.id);
      }
      const undrawn = [...currentByTicket.values()]
        .filter((loc) => (loc.kind === 'BAY' || loc.kind === 'PARKING_SPOT') && !drawn.has(loc.ticketId))
        .map((loc) => loc.ticketId);
      if (undrawn.length > 0) {
        const extra = await supabase.from('tickets').select(ticketSelect).eq('shop_id', activeShop.id).in('id', undrawn);
        if (extra.error) logSupabaseError('birdseye_tickets_physical_occupants', extra.error);
        else unifiedRows.push(...((extra.data || []) as any[]));
      }
    }
    unifiedRows.forEach((t) => {
      const holderId: string = t.location_parent_id ?? t.location_rollover_id ?? t.id;
      ticketMap.set(t.id, currentByTicket
        ? placeByPhysicalTruth(t, currentByTicket, holderId)
        : placeByLegacyProjection(t, holderId));
    });

    const transformed = Array.from(ticketMap.values()).map(t => ({
      ...t,
      jobs: t.ticket_jobs?.map((tj: any) => ({
        ...tj.job,
        name: tj.job.name,
        standardMinutes: tj.minutes_override || tj.job.standard_minutes,
        notes: tj.notes
      })) || [],
      vehiclePresent: isVehiclePresent(t),
      predictedMinutes: t.predicted_minutes,
      assignedTechId: t.assigned_tech_id,
      pauseReason: t.pause_reason,
      customerName: t.customer_name,
      startTime: t.start_time,
      actualStartTime: t.actual_start_time,
      actuallyStarted: t.actually_started,
      timerStatus: t.timer_status,
      optiTime: t.opti_time,
      optiFinish: t.opti_finish,
      runLongMinutes: t.run_long_minutes,
    }));
    // Enrich tickets with parking spot labels via client-side lookup.
    // Batch 10b: read parking spots from `parkingSpotsRef.current`
    // instead of closing over `parkingSpots` so the callback identity
    // is stable across parking-spot refreshes — the realtime channel
    // useEffect (deps include this callback) no longer tears down +
    // resubscribes on every `loadParkingSpots()` setState.
    const currentParkingSpots = parkingSpotsRef.current;
    const enriched = transformed.map(t => {
      if (!t.parking_spot_id) return { ...t, parking_spot: null };
      const spot = currentParkingSpots.find(ps => ps.id === t.parking_spot_id);
      return { ...t, parking_spot: spot ? { id: spot.id, label: spot.label } : null };
    });
    if (!ticketsGate.commit(token)) return;
    setTicketLoadError(null);
    setTickets(enriched);
  }, [activeShop?.id, selectedDayLabel]);

  const loadTechnicians = useCallback(async () => {
    if (!activeShop) return;
    const { data } = await supabase.from('technicians').select('*').eq('shop_id', activeShop.id).eq('employment_status', 'Active');
    if (data) setTechnicians(data);
  }, [activeShop?.id]);

  // Stage 0D — one authoritative reload of everything the floor renders.
  // Parking spots load FIRST: loadTickets labels each parked ticket from
  // parkingSpotsRef, so loading them in parallel left parked cars unlabelled.
  const refreshFloor = useCallback(async () => {
    await loadParkingSpots();
    await Promise.all([loadBays(), loadTickets(), loadTechnicians()]);
    // Core-100 R4 (CORE-X): when the floor last came from the server — shown while live updates are paused.
    setLastRefreshedAt(clock.now());
  }, [loadBays, loadParkingSpots, loadTickets, loadTechnicians]);

  // Stage 0D — server truth again when the phone / tab comes back.
  useForegroundRecovery(() => { void refreshFloor(); }, { enabled: !!activeShop });

  // Stage 0D — bumped to re-subscribe after the client drops the channel.
  const [realtimeEpoch, setRealtimeEpoch] = useState(0);
  // Consecutive abnormal closes, for the bounded re-subscribe backoff.
  const rebuildAttemptsRef = useRef(0);
  // Core-100 R4 (CORE-X): "a stale picture is worse than no picture". While the channel is down the map SAYS it may
  // be out of date (since when, last refreshed when), and once the bounded backoff gives up it says it is no longer
  // reconnecting on its own and offers a Refresh that reloads AND re-subscribes.
  const [liveState, setLiveState] = useState<LiveState>({ live: true });
  const [realtimeGaveUp, setRealtimeGaveUp] = useState(false);
  const reconnectFloor = useCallback(() => {
    rebuildAttemptsRef.current = 0;
    setRealtimeGaveUp(false);
    void refreshFloor();
    setRealtimeEpoch((n) => n + 1);
  }, [refreshFloor]);
  // Coming back to the tab also re-subscribes a channel the backoff gave up on.
  useForegroundRecovery(() => { if (realtimeGaveUp) reconnectFloor(); }, { enabled: !!activeShop && realtimeGaveUp });

  useEffect(() => {
    if (!activeShop) return;

    const loadAll = async () => {
      try {
        await refreshFloor();
      } finally {
        setIsLoading(false);
      }
    };
    loadAll();

    // Scoped realtime with targeted refresh.
    //
    // RTC-A3: collapse the three pre-A3 page-level channels
    //   (`birdseye-bays-...`, `birdseye-tickets-...`, `birdseye-parking-...`)
    // into ONE consolidated channel with three `.on('postgres_changes', ...)`
    // listener registrations. Pre-A3 each Birdseye mount opened THREE
    // websocket subscriptions for the same shop scope and the same
    // useEffect lifecycle — every event therefore traversed three
    // independent channel objects. Post-A3 the same three listener
    // bodies are registered against ONE channel; behavior is preserved
    // verbatim because Supabase JS dispatches each
    // `.on('postgres_changes', ...)` listener independently regardless
    // of how many channels host it. The listener bodies below are
    // byte-identical to the pre-A3 versions (including the RFS-A2
    // 300ms debounce wrapper, the RFS-A3 11-field scheduling gate, the
    // FIX-R34/R35/R36 markers, the client-side shop guard, and the
    // simple `() => loadBays()` / `() => loadParkingSpots()` refetch
    // closures); only the channel-creation + `.subscribe(...)` +
    // `removeChannel(...)` boilerplate was consolidated 3→1. The
    // status callback is also consolidated: pre-A3 each of the three
    // `.subscribe(...)` calls had its own `(status, err) =>
    // console.error(...)` log line; post-A3 a SINGLE callback covers
    // all three listeners. The pre-A3 per-listener log identifiers
    // are preserved in this doctrinal block (FIX-R36-LOW-1 ×2 + FIX-
    // R35-MED-1) so a future regression that re-introduces per-channel
    // `.subscribe` can re-anchor on the original markers.
    //
    // OUT OF SCOPE — explicitly NOT merged in this round:
    //   - `useLiveReshuffle` (different filter axis: `scheduled_date`,
    //     not `shop_id`; date-driven cascade lifecycle is intentionally
    //     separate). Runs on its own channel `schedule-changes-...`.
    //   - `useReminderTriggers` / `ReminderTriggerProvider`
    //     (App.tsx-mounted around the page, not page-mounted).
    //
    // Pre-A3 channel names (preserved here as a doctrinal anchor for
    // the closure test §A — the test scans for them as REMOVED):
    //   `birdseye-bays-${shop_id}`
    //   `birdseye-tickets-${shop_id}`
    //   `birdseye-parking-${shop_id}`
    //
    // Post-A3 channel name:
    //   `birdseye-realtime-${shop_id}`
    // Pre-A3 none of the three channels included `selectedDayLabel`
    // in their name (only the useEffect deps drove the date-rebuild
    // lifecycle). Post-A3 keeps that naming axis: shop_id only. The
    // useEffect deps remain unchanged, so the merged channel still
    // tears down + recreates on date change exactly as the three
    // pre-A3 channels did.

    // RFS-A2: debounce loadTickets refetch on the tickets channel. Pre-A2
    // every ticket UPDATE/INSERT/DELETE fired a full date-scoped
    // `loadTickets()` (~50-200 rows × 5 joins). Under cascade fan-out
    // (useLiveReshuffle triggers cascade-board → 10-50 ticket UPDATEs in
    // <5s) this caused 30+ sequential full refetches and 5-15s of frozen
    // UI on Birdseye. Debouncing collapses bursts into one refetch
    // window-of-quiet later. Window 300ms = imperceptible user-visible
    // staleness; well within useLiveReshuffle's 10s cascade-debounce
    // upstream (so cascade waves are still serialized via the gold pattern).
    // (RTC-A3: declaration site preserved BEFORE the channel chain so
    // the helper is hoisted into the listener closure unchanged.)
    let ticketsRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleTicketsRefetch = () => {
      if (ticketsRefetchTimer) clearTimeout(ticketsRefetchTimer);
      ticketsRefetchTimer = setTimeout(() => {
        ticketsRefetchTimer = null;
        loadTickets();
      }, 300);
    };
    // Stage 0D — after a (re)join, events may have been missed: reload all.
    let floorRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleFloorRefetch = () => {
      if (floorRefetchTimer) clearTimeout(floorRefetchTimer);
      floorRefetchTimer = setTimeout(() => {
        floorRefetchTimer = null;
        void refreshFloor();
      }, 300);
    };
    let rebuildTimer: ReturnType<typeof setTimeout> | null = null;

    const birdseyeChannel = supabase
      .channel(uniqueChannelTopic(`birdseye-realtime-${activeShop.id}`))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shop_bays', filter: `shop_id=eq.${activeShop.id}` }, () => loadBays())
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tickets',
        // Server-side scope: only stream events for this shop. Cuts cross-
        // shop noise, bandwidth, and reduces the chance of a ticket from a
        // sibling shop briefly leaking into the channel before the
        // client-side guard rejects it. The client guard below is kept as
        // defense-in-depth.
        filter: `shop_id=eq.${activeShop.id}`,
      }, (payload) => {
        // Client-side shop guard (defence-in-depth)
        const row = (payload.new || payload.old) as any;
        if (!row?.shop_id || row.shop_id !== activeShop.id) return;

        // RFS-A3 / Stage 0D: rendered-field gate. UPDATEs that touch none
        // of the fields Birdseye renders skip the refetch; INSERT and DELETE
        // always refetch. The field set lives in one place
        // (FLOOR_TICKET_FIELDS) and is shared with ShopFloor.
        if (!floorTicketChangeMatters(payload)) return;

        // RFS-A2: schedule a debounced refetch instead of firing immediately.
        scheduleTicketsRefetch();
      })
      // Stage 0D: a spot's label rides on each parked ticket (loadTickets reads
      // parkingSpotsRef), so a spot change reloads the tickets after the spots.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parking_lot_spots', filter: `shop_id=eq.${activeShop.id}` }, () => { void loadParkingSpots().then(scheduleTicketsRefetch); })
      // Stage B/F: a physical move changes current truth; that is what moves
      // a card. (Published with REPLICA IDENTITY FULL — an unpublished table
      // would silence this whole channel, Stage 0D.)
      // #15: every location writer (move, correction, Guided Start pull-in,
      // Final Stop, pickup) lands here — and so will any future one — so
      // Birdseye needs no per-workflow signal. Shop guard as for tickets.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_location_current', filter: `shop_id=eq.${activeShop.id}` }, (payload) => {
        const row = (payload.new || payload.old) as any;
        if (row?.shop_id && row.shop_id !== activeShop.id) return;
        scheduleTicketsRefetch();
      })
      .subscribe((status, err) => {
        // RTC-A3: consolidated status callback covers all three
        // listeners (shop_bays / tickets / parking_lot_spots). Pre-A3
        // each of the three channels had its own `(status, err) =>
        // console.error(...)` callback — FIX-R36-LOW-1 (shop_bays) +
        // FIX-R35-MED-1 (tickets) + FIX-R36-LOW-1 (parking_lot_spots).
        // Post-A3 a single callback surfaces drops for the entire
        // merged channel; the listener-binding error is carried in
        // `err`. All three pre-A3 doctrinal markers are preserved in
        // the doctrinal block above so future regression detection
        // can re-anchor.
        // Batch 10: CLOSED with `err === undefined` is the normal
        // unmount / shop-switch cleanup path (we call
        // `supabase.removeChannel(birdseyeChannel)` in the effect
        // teardown below; supabase-js emits CLOSED back to this
        // callback). Surface it at console.debug so verbose-level
        // operators can still see it but the default console isn't
        // flooded. CHANNEL_ERROR / TIMED_OUT / CLOSED-with-err are
        // still console.error — those are genuinely abnormal.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || (status === 'CLOSED' && err !== undefined)) {
          console.error('[Birdseye] realtime channel status:', status, err);
        } else if (status === 'CLOSED') {
          console.debug('[Birdseye] realtime channel closed (normal unmount/cleanup)');
        }
        // Stage 0D: realtime is a signal, not the truth. Every (re)join
        // reloads what may have been missed; a channel the client dropped
        // for good is re-subscribed on a fresh topic.
        const action = realtimeRecoveryAction(status, err);
        const abnormal = status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || (status === 'CLOSED' && err !== undefined);
        setLiveState((prev) => nextLiveState(prev, String(status), abnormal, clock.now()));
        if (action === 'refetch') {
          rebuildAttemptsRef.current = 0;
          scheduleFloorRefetch();
          setRealtimeGaveUp(false);
        }
        if (action === 'rebuild' && !rebuildTimer) {
          const delay = rebuildDelayMs(rebuildAttemptsRef.current);
          if (delay !== null) {
            rebuildAttemptsRef.current += 1;
            rebuildTimer = setTimeout(() => setRealtimeEpoch((n) => n + 1), delay);
          } else {
            setRealtimeGaveUp(true);
          }
        }
      });

    return () => {
      // RFS-A2: clear any pending debounced refetch on unmount /
      // shop-or-date change so the next mount starts with a clean timer
      // and no stale loadTickets() from the previous shop fires.
      if (ticketsRefetchTimer) clearTimeout(ticketsRefetchTimer);
      if (floorRefetchTimer) clearTimeout(floorRefetchTimer);
      if (rebuildTimer) clearTimeout(rebuildTimer);
      supabase.removeChannel(birdseyeChannel);
    };
  }, [activeShop?.id, selectedDayLabel, loadBays, loadParkingSpots, loadTickets, loadTechnicians, refreshFloor, realtimeEpoch]);

  const loadData = async () => {
    if (!activeShop) return;
    try {
      await refreshFloor();
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading shop floor data:', error);
      setIsLoading(false);
    }
  };

  const bayTypeColors: Record<string, string> = {
    '2-post': 'hsl(210, 100%, 50%)',
    '4-post': 'hsl(260, 100%, 60%)',
    'scissor': 'hsl(140, 100%, 45%)',
    'single-post': 'hsl(180, 80%, 45%)',
    'adas': 'hsl(30, 100%, 50%)',
    'alignment': 'hsl(340, 100%, 55%)',
    'wash': 'hsl(190, 100%, 50%)',
    'inspection': 'hsl(50, 100%, 55%)',
    'general': 'hsl(0, 0%, 50%)',
    'flat': 'hsl(0, 0%, 50%)',
  };

  // Batch 16-A: labels are derived from the canonical
  // `BAY_TYPE_PICKER_OPTIONS` in `src/utils/bayTypeLabels.ts`. Pre-bump
  // this map was hand-maintained and had no entry for `'alignment-adas'`,
  // so any bay with that stored value rendered the raw enum string in
  // both the bay card and the BayDetailSheet (the prop downstream is
  // `Record<string, string>`). Sourcing from the canonical helper closes
  // that gap and locks every Birdseye label render to the same labels the
  // InspectorPanel, ShopFloorEditor, and wizard surface.
  const bayTypeLabels: Record<string, string> = Object.fromEntries(
    BAY_TYPE_PICKER_OPTIONS.map(o => [o.value, o.label]),
  );

  const bayCategoryColors: Record<string, string> = {
    'work-area': 'hsl(45, 90%, 55%)',
    'storage': 'hsl(280, 70%, 60%)',
    'front-of-house': 'hsl(160, 70%, 50%)'
  };

  // Birdseye renders bays via getBayNowAndNextTickets() (bayScheduleHelpers.ts)
  // which correctly handles Active + Paused + actually-started tickets. The
  // previous local getBayTicket / getActiveTicketForTech helpers were unused
  // and filtered on status === 'Active' only, which would have hidden paused
  // vehicles still physically in the bay. Removed to prevent accidental reuse.

  const getTechName = (techId: string) => {
    return technicians.find(t => t.id === techId)?.name || 'Unassigned';
  };

  const handleBaySelect = (bayId: string) => {
    // Don't open panel if user just dragged
    if (camera.consumeDrag()) return;
    
    // Open sidebar immediately with existing data
    setSelectedBayId(bayId);
    
    // Refresh data in background (non-blocking)
    loadData();
  };

  // Auto-fit on initial load
  useEffect(() => {
    if (!isLoading && (bays.length > 0 || parkingSpots.length > 0) && !hasFittedRef.current) {
      const timeout = setTimeout(() => {
        autoFit();
        hasFittedRef.current = true;
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, bays.length, parkingSpots.length, autoFit]);

  // OCT28 RECOVERY #4: a rotation, a browser bar or a panel changing the map's size refits it — unless the person
  // panned or zoomed it themselves (that view is theirs).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      if (!hasFittedRef.current || camera.hasUserMoved()) return;
      clearTimeout(t);
      t = setTimeout(autoFit, 120);
    });
    ro.observe(el);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [containerRef, autoFit, camera, isLoading]);

  // Reset fit flag when shop changes
  useEffect(() => {
    hasFittedRef.current = false;
  }, [activeShop?.id]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // dnd-kit handlers for ticket bay moves
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);
    if (activeId.startsWith('ticket:')) {
      const ticketId = activeId.replace('ticket:', '');
      setActiveTicketId(ticketId);
      setIsDraggingTicket(true);
    }
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Permission check: only managers/writers/dispatchers can drag
    if (!canAssignTickets) {
      setIsDraggingTicket(false);
      setActiveTicketId(null);
      return;
    }
    
    setIsDraggingTicket(false);
    setActiveTicketId(null);
    
    if (!over) return; // Dropped outside - no-op
    
    const activeId = String(active.id);
    const overId = String(over.id);
    
    // Only process ticket drops
    if (!activeId.startsWith('ticket:')) return;
    
    const ticketId = activeId.replace('ticket:', '');
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;
    
    // Determine target type: bay or parking spot
    const isBayTarget = overId.startsWith('bay:');
    const isSpotTarget = overId.startsWith('spot:');
    
    if (!isBayTarget && !isSpotTarget) return;
    
    const targetId = isBayTarget 
      ? overId.replace('bay:', '') 
      : overId.replace('spot:', '');
    
    // Same-location no-op
    if (isBayTarget && ticket.bay_id === targetId) return;
    if (isSpotTarget && ticket.parking_spot_id === targetId) return;

    // #15: a spot or bay that current truth already fills is not offered,
    // whatever colour the card had when the drag began. Advisory only — the
    // server re-decides under its lock (a stale view is refused there).
    const occupiedRefusal = floorDropRefusal(
      isBayTarget ? { kind: 'BAY', bayId: targetId } : { kind: 'PARKING_SPOT', parkingSpotId: targetId },
      ticket,
      occupancy,
    );
    if (occupiedRefusal) {
      toast({ title: 'Cannot move ticket', description: occupiedRefusal, variant: 'destructive' });
      void loadData();
      return;
    }

    // Client-side early validation for bay targets (Fix 3)
    if (isBayTarget) {
      const targetBay = bays.find(b => b.id === targetId);
      if (targetBay) {
        const safetyResult = validateLiftSafety(ticket.gvwr_lbs, targetBay.lift_capacity_lbs);
        if (!safetyResult.safe) {
          // Show warning dialog with locally-computed compatible lifts
          const localCompatible = bays
            .filter(b => b.id !== targetId && b.is_active && b.lift_capacity_lbs != null && b.lift_capacity_lbs >= ticket.gvwr_lbs)
            .map(b => ({ bayId: b.id, bayName: b.name, liftCapacityLbs: b.lift_capacity_lbs, bayType: b.bay_type }))
            .sort((a, b) => a.liftCapacityLbs - b.liftCapacityLbs);
          setLiftWarningData({
            gvwr: safetyResult.gvwr!,
            capacity: safetyResult.capacity!,
            compatibleLifts: localCompatible,
            ticketId,
          });
          return;
        }

        // Chronomancer R-BAY-5: bay-type / capability gating pre-check.
        // Previously drag-drop validated ONLY lift safety — a ticket
        // requiring an alignment rack could be dropped on a 2-post bay
        // with no warning, the move_vehicle_location RPC would succeed
        // (it doesn't gate on bay_type), and the ticket would sit in an
        // incompatible bay until a human noticed. This uses the same
        // canonical validator (`bayGatingValidator`) that smart-assign
        // and reassignCore use, keeping UI and automation in lockstep.
        // A confirm dialog lets the user force-override (consistent with
        // the manual-override pattern); a cancel aborts the drop.
        const jobSnapshots = (ticket.ticket_jobs || []).map((tj: any) => ({
          allowed_bay_types_snapshot: tj.allowed_bay_types_snapshot || null,
          required_capabilities_snapshot: tj.required_capabilities_snapshot || null,
          excluded_bay_types_snapshot: tj.excluded_bay_types_snapshot || null,
        }));
        const requirements = computeAllowedBaysForTicket(jobSnapshots);
        if (requirements.hasRequirements && targetBay.bay_type) {
          const compatible = isTechBayCompatible([targetBay.bay_type], requirements);
          if (!compatible) {
            const reason = formatBayRejectionReason(requirements);
            const proceed = window.confirm(
              `${reason}\n\nTarget bay "${targetBay.name}" (${targetBay.bay_type}) does not meet the ticket's bay requirements. Force-place anyway?`
            );
            if (!proceed) {
              return;
            }
          }
        }
      }
    }

    setIsMovingBay(true);
    
    try {
      // Call RPC with appropriate target
      const rpcParams: any = { p_ticket_id: ticketId };
      if (isBayTarget) {
        rpcParams.p_target_bay_id = targetId;
      } else {
        rpcParams.p_target_parking_spot_id = targetId;
      }
      
      const { data: rawData, error } = await supabase.rpc('move_vehicle_location', rpcParams);
      
      if (error) throw error;
      
      const result = rawData as unknown as { success: boolean; reason?: string; liftSafetyBlock?: boolean; liftSafetyUnverified?: boolean; liftRatingUnknown?: boolean; gvwr?: number; capacity?: number; compatibleLifts?: Array<{ bayId: string; bayName: string; liftCapacityLbs: number; bayType?: string }> };
      if (result && !result.success) {
        // OD-33 (revised 2026-10-15): an unrated lift no longer refuses — the server allows the move and marks it
        // capacityNotVerified (warned on success below). Unknown vehicle weight on a RATED lift still asks for a
        // manager's verification (OD-22); a known overweight is still refused.
        if (result.liftSafetyUnverified && isBayTarget) {
          setLiftVerify({ ticketId, bayId: targetId, bayName: bays.find(b => b.id === targetId)?.name });
        } else if (result.liftSafetyBlock) {
          // Show warning dialog with server-authoritative compatible lifts
          setLiftWarningData({
            gvwr: result.gvwr!,
            capacity: result.capacity!,
            compatibleLifts: result.compatibleLifts || [],
            ticketId,
          });
        } else {
          toast({
            title: 'Cannot move ticket',
            description: result.reason || 'Location incompatible',
            variant: 'destructive',
          });
        }
        // #15: a refusal means this view was behind ("already occupied") —
        // re-read the truth instead of waiting for the realtime echo.
        void loadData();
        return;
      }
      
      // Success - reload data from server (no optimistic updates)
      if (isBayTarget) {
        const targetBay = bays.find(b => b.id === targetId);
        if (isCapacityNotVerified(result)) {
          // OD-33: allowed, never presented as safe.
          toast({ title: CAPACITY_NOT_VERIFIED_TITLE, description: capacityNotVerifiedDescription(targetBay?.name) });
        } else {
          toast({
            title: 'Bay Updated',
            description: `Ticket moved to ${targetBay?.name || 'new bay'}`,
          });
        }
      } else {
        const targetSpot = parkingSpots.find(s => s.id === targetId);
        toast({
          title: 'Parked',
          description: `Ticket moved to parking spot P${targetSpot?.label || ''}`,
        });
      }
      
      // Reload fresh data from server
      await loadData();
      
      // Clear move mode if it was set
      if (moveTicketId === ticketId) {
        setSearchParams(prev => {
          prev.delete('moveTicketId');
          return prev;
        });
      }
    } catch (error: any) {
      console.error('[BIRDSEYE] Failed to move ticket:', error);
      toast({
        title: 'Failed to move ticket',
        description: error?.message || 'An error occurred',
        variant: 'destructive',
      });
      void loadData();
    } finally {
      setIsMovingBay(false);
    }
  }, [tickets, occupancy, bays, parkingSpots, moveTicketId, toast, setSearchParams]);

  // Auto-pan to ticket being moved
  useEffect(() => {
    if (moveTicketId && containerRef.current && ticketToMove) {
      const bay = bays.find(b => b.id === ticketToMove.bay_id);
      if (bay) {
        const container = containerRef.current;
        const viewportW = container.clientWidth;
        const viewportH = container.clientHeight;
        const contentX = bay.position_x - bounds.minX;
        const contentY = bay.position_y - bounds.minY;
        camera.panTo({
          x: viewportW / 2 - contentX * zoomRef.current,
          y: viewportH / 2 - contentY * zoomRef.current,
        });
      }
    }
  }, [moveTicketId, ticketToMove, bays, bounds]);


  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Loading shop floor...</p>
        </main>
      </div>
    );
  }

  // Get active ticket for drag overlay
  const activeTicket = activeTicketId ? tickets.find(t => t.id === activeTicketId) : null;

  return (
    <DndContext 
      sensors={sensors} 
      onDragStart={handleDragStart} 
      onDragEnd={handleDragEnd}
    >
      <div className="relative flex flex-col h-screen bg-background overflow-hidden">
        <div className="tech-grid" />
        <div className="relative z-10 flex flex-col h-full">
        <TopBar />
        
        <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <LiveUpdatesBanner
            state={liveState}
            subject="the floor map"
            reconnecting={!realtimeGaveUp}
            lastRefreshedAt={lastRefreshedAt}
            onRefresh={reconnectFloor}
          />
          {/* Batch 10: ticket load failure banner. Lights up only
              when the most recent loadTickets() returned a non-null
              PostgREST error. Operator sees an honest "couldn't
              load" message instead of an empty/zoom-stuck Birdseye
              and is told to check the console for the structured
              error (code + message + details + hint logged by
              logSupabaseError). Banner is dismissable; the next
              successful loadTickets clears state automatically. */}
          {ticketLoadError && (
            <div
              className="bg-amber-500/10 border-b border-amber-500/30 px-6 py-2 flex items-center gap-3 text-sm"
              data-testid="birdseye-ticket-load-error-banner"
              role="alert"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="text-amber-900 dark:text-amber-200">
                Couldn't load tickets — see browser console for details.
              </span>
              <span className="text-xs text-amber-700 dark:text-amber-300 truncate">
                {ticketLoadError}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 px-2"
                onClick={() => setTicketLoadError(null)}
              >
                Dismiss
              </Button>
            </div>
          )}
          {/* Move Mode Banner */}
          {moveTicketId && ticketToMove && (
            <div className="bg-blue-500/10 border-b border-blue-500/20 px-6 py-3 flex items-center gap-3">
              <Move className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Drag the highlighted ticket to a different bay
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto"
                onClick={() => setSearchParams(prev => { prev.delete('moveTicketId'); return prev; })}
              >
                Cancel
              </Button>
            </div>
          )}
          
          {/* Header */}
          <div className="border-b bg-card px-4 py-3 sm:px-6 sm:py-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h1 className="text-2xl font-bold">Birdseye View</h1>
              <p className="text-sm text-muted-foreground">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fitToScreen()}
                className="text-xs"
              >
                <Maximize2 className="h-4 w-4 mr-2" />
                Fit to Screen
              </Button>
              {formFactor === 'desktop' && (
                <Button
                  variant={isFullscreen ? "default" : "outline"}
                  size="sm"
                  onClick={toggleFullscreen}
                  className="text-xs"
                >
                  {isFullscreen ? <Minimize className="h-4 w-4 mr-2" /> : <Maximize className="h-4 w-4 mr-2" />}
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
              )}
              <Button onClick={() => navigate('/shop-floor/editor')} data-testid="configure-layout-btn">
                <Settings className="h-4 w-4 mr-2" />
                Configure Layout
              </Button>
            </div>
          </div>

          {/* Shop Floor Grid with Not Yet Arrived Panel */}
          {/* OCT28 RECOVERY #4: below lg the location-conflicts list sits ABOVE the map (capped height) instead of beside
              it — as a flex sibling it squeezed a phone's map to 0 px wide. */}
          <div className="relative flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
            <div
              ref={containerRef}
              className="flex-1 min-h-0 min-w-0 overflow-hidden bg-muted/20"
              style={{ cursor: isDragging || isDraggingTicket ? 'grabbing' : 'grab', touchAction: 'none' }}
              {...camera.handlers}
              data-testid="birdseye-grid"
            >
              {bays.length === 0 && parkingSpots.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <p className="text-lg text-muted-foreground mb-4">No bays or parking spots configured yet</p>
                  <Button onClick={() => navigate('/shop-floor/editor')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configure Shop Layout
                  </Button>
                </div>
              ) : (
                <div
                  className="relative will-change-transform"
                  style={{
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                    transformOrigin: '0 0',
                    width: `${bounds.width}px`,
                    height: `${bounds.height}px`,
                  }}
                >
                  {/* Bay Cards */}
                  {bays.map(bay => {
                    const { currentTicket, nextTicket } = getBayNowAndNextTickets(bay.id, tickets, clock.now());
                    const tech = technicians.find(t => t.id === bay.assigned_tech_id);

                    return (
                      <BirdseyeBayCard
                        key={bay.id}
                        bay={{
                          ...bay,
                          position_x: bay.position_x - bounds.minX,
                          position_y: bay.position_y - bounds.minY
                        }}
                        currentTicket={currentTicket}
                        nextTicket={nextTicket}
                        technician={tech}
                        technicians={technicians}
                        bayTypeColors={bayTypeColors}
                        bayTypeLabels={bayTypeLabels}
                        bayCategoryColors={bayCategoryColors}
                        onClick={() => handleBaySelect(bay.id)}
                        moveTicketId={moveTicketId}
                        isDraggingOver={false}
                        shopTimezone={shopTimezone}
                        readOnly={!canAssignTickets}
                      />
                    );
                  })}

                  {/* Parking Spot Cards */}
                  {parkingSpots.map(spot => {
                    // Chronomancer R-LOC-6: when multiple tickets share a
                    // parking_spot_id (only legal case is a chain's dependents
                    // drawn where its car is), prefer the ticket that holds
                    // the car's truth. #15: that choice lives in the shared
                    // occupancy projection; the old inline check read
                    // `rollover_of_ticket_id`, which this page selects under
                    // an alias, so it never matched.
                    const parkedTicket = occupancy.spotOccupant.get(spot.id) ?? null;
                    return (
                      <BirdseyeParkingSpotCard
                        key={`spot-${spot.id}`}
                        spot={{
                          ...spot,
                          position_x: spot.position_x - bounds.minX,
                          position_y: spot.position_y - bounds.minY,
                        }}
                        ticket={parkedTicket || null}
                        onClick={() => setSelectedSpotId(spot.id)}
                        readOnly={!canAssignTickets}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Core-100 R4 CORE-Z: a named place another vehicle physically held — recorded, now shown */}
            {activeShop && (
              <div className="order-first shrink-0 max-h-28 overflow-y-auto empty:hidden lg:order-none lg:max-h-none lg:w-72" data-testid="location-conflicts-slot">
                <LocationIngestConflictsPanel
                  shopId={activeShop.id}
                  bayNames={new Map(bays.map((b: { id: string; name: string }) => [b.id, b.name]))}
                  spotNames={new Map(parkingSpots.map((s: { id: string; label: string }) => [s.id, s.label]))}
                />
              </div>
            )}
            {/* Not Yet Arrived Panel */}
            <NotYetArrivedPanel tickets={tickets} selectedDate={selectedDate} readOnly={!canAssignTickets} />
          </div>

          {/* Bay Detail Sheet */}
          <BayDetailSheet
            bay={bays.find(b => b.id === selectedBayId) || null}
            tickets={tickets}
            technicians={technicians}
            bays={bays}
            bayTypeLabels={bayTypeLabels}
            isOpen={!!selectedBayId}
            onClose={() => setSelectedBayId(null)}
            onTicketReassigned={loadData}
            selectedDate={selectedDate}
            readOnly={!canAssignTickets}
          />

          {/* Parking Spot Detail Sheet */}
          <ParkingSpotDetailSheet
            spot={parkingSpots.find(s => s.id === selectedSpotId) || null}
            // Chronomancer R-LOC-6: same chain-root preference as the spot
            // render above so the detail sheet describes the same vehicle
            // the card showed.
            ticket={selectedSpotId ? occupancy.spotOccupant.get(selectedSpotId) ?? null : null}
            bays={bays}
            isOpen={!!selectedSpotId}
            onClose={() => setSelectedSpotId(null)}
            onRefresh={loadData}
            readOnly={!canAssignTickets}
          />

          {/* CORE-AU: weight not verified for this lift — a manager verifies, everyone else is told to ask. */}
          {liftVerify && (
            <LiftVerificationDialog
              open={!!liftVerify}
              onOpenChange={(o) => { if (!o) setLiftVerify(null); }}
              ticketId={liftVerify.ticketId}
              bayId={liftVerify.bayId}
              bayName={liftVerify.bayName}
              canVerify={!!canEditShopFloor}
              onVerified={() => {
                toast({ title: 'Lift verified', description: 'The vehicle may now go on this lift — move it again.' });
                void loadData();
              }}
            />
          )}

          {/* Lift Safety Warning Dialog */}
          <LiftCapacityWarning
            open={!!liftWarningData}
            onClose={() => setLiftWarningData(null)}
            gvwr={liftWarningData?.gvwr ?? 0}
            capacity={liftWarningData?.capacity ?? 0}
            compatibleLifts={liftWarningData?.compatibleLifts}
            onSelectLift={async (bayId) => {
              if (!liftWarningData) return;
              setLiftWarningData(null);
              setIsMovingBay(true);
              try {
                const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
                  p_ticket_id: liftWarningData.ticketId,
                  p_target_bay_id: bayId,
                });
                if (error) throw error;
                const result = rawData as unknown as { success: boolean; reason?: string };
                if (result && !result.success) {
                  toast({ title: 'Cannot move ticket', description: result.reason || 'Move failed', variant: 'destructive' });
                  void loadData();
                  return;
                }
                const targetBay = bays.find(b => b.id === bayId);
                toast({ title: 'Bay Updated', description: `Ticket moved to ${targetBay?.name || 'new bay'}` });
                await loadData();
              } catch (err: any) {
                toast({ title: 'Failed to move ticket', description: err?.message || 'An error occurred', variant: 'destructive' });
              } finally {
                setIsMovingBay(false);
              }
            }}
          />
        </main>
        </div>
        
        {/* Drag Overlay */}
        <DragOverlay>
          {activeTicket && (
            <div className="bg-card border-2 border-primary rounded-lg p-3 shadow-xl opacity-90 max-w-[200px]">
              <p className="font-semibold text-sm truncate">
                {activeTicket.vehicle?.year} {activeTicket.vehicle?.make} {activeTicket.vehicle?.model}
              </p>
              <p className="text-xs text-muted-foreground truncate">{activeTicket.customerName}</p>
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default Birdseye;
